import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, Brain, FileText, Trash2,
  Download, RefreshCw, ChevronRight, AlertTriangle,
  CheckCircle2, XCircle, Clock, FileBarChart, BookOpen
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import FileUploadZone from "../components/FileUploadZone";
import DiaryPage from "./DiaryPage";
import ProjectChat from "../components/ProjectChat";
import {
  getProject, getUploads, uploadFiles, deleteUpload,
  runAnalysis, getAnalyses, generateReport, getReports,
  getReport, exportReport, regenerateSection
} from "../lib/api";

const SECTION_LABELS = {
  test_overview: "Test Overview",
  scope_objectives: "Scope & Objectives",
  applicable_requirements: "Applicable Requirements",
  test_environment: "Test Environment",
  test_method: "Test Method & Procedure",
  results_summary: "Results Summary",
  detailed_results: "Detailed Results",
  deviations_anomalies: "Deviations & Anomalies",
  failure_analysis: "Failure & Root Cause Analysis",
  safety_impact: "Safety Impact Assessment",
  compliance_evidence: "Compliance Evidence",
  conclusions: "Conclusions",
  recommendations: "Recommendations & Next Steps",
  diary_notes: "Engineering Diary Notes",
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState({});
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("files");

  const fetchAll = async () => {
    try {
      const [projRes, uploadsRes, analysesRes, reportsRes] = await Promise.all([
        getProject(projectId),
        getUploads(projectId),
        getAnalyses(projectId),
        getReports(projectId),
      ]);
      setProject(projRes.data);
      setUploads(uploadsRes.data);
      setAnalyses(analysesRes.data);
      setReports(reportsRes.data);
    } catch {
      toast.error("Failed to load project data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [projectId]);

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      pendingFiles.forEach((f) => formData.append("files", f));
      const descs = pendingFiles.map((_, i) => fileDescriptions[i] || "");
      formData.append("descriptions", JSON.stringify(descs));
      await uploadFiles(projectId, formData);
      toast.success(`${pendingFiles.length} file(s) uploaded`);
      setPendingFiles([]);
      setFileDescriptions({});
      fetchAll();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteUpload(projectId, fileId);
      toast.success("File deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete file");
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await runAnalysis(projectId);
      toast.success("Analysis completed");
      setActiveTab("analysis");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const res = await generateReport(projectId);
      toast.success("Report generated");
      setActiveTab("reports");
      setActiveReport(res.data);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Report generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleViewReport = async (reportId) => {
    try {
      const res = await getReport(projectId, reportId);
      setActiveReport(res.data);
    } catch {
      toast.error("Failed to load report");
    }
  };

  const handleExport = async (reportId, format) => {
    try {
      const res = await exportReport(projectId, reportId, format);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${reportId}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleRegenerateSection = async (reportId, sectionName) => {
    setRegenerating(sectionName);
    try {
      const res = await regenerateSection(projectId, reportId, sectionName);
      setActiveReport(res.data);
      toast.success(`Section "${SECTION_LABELS[sectionName]}" regenerated`);
    } catch {
      toast.error("Regeneration failed");
    } finally {
      setRegenerating(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="project-loading">
        <div className="h-8 w-64 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-200 rounded-sm" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-16" data-testid="project-not-found">
        <p className="text-slate-500">Project not found</p>
        <Link to="/projects">
          <Button variant="outline" className="mt-4 rounded-sm">Back to Projects</Button>
        </Link>
      </div>
    );
  }

  const latestAnalysis = analyses[0] || null;

  return (
    <div className="space-y-6" data-testid="project-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/projects")}
            className="text-slate-500 hover:text-[#003057] -ml-2 mb-1"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Projects
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-[#003057]" data-testid="project-name">
            {project.name}
          </h1>
          {project.description && (
            <p className="text-sm text-slate-500 mt-1">{project.description}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {project.vehicle_platform && (
              <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 rounded-sm">
                Platform: {project.vehicle_platform}
              </Badge>
            )}
            {project.ecu && (
              <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 rounded-sm">
                ECU: {project.ecu}
              </Badge>
            )}
            {project.sw_version && (
              <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 rounded-sm">
                SW: {project.sw_version}
              </Badge>
            )}
            {project.hpcu_sw && (
              <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 rounded-sm">
                HPCU: {project.hpcu_sw}
              </Badge>
            )}
            {project.baseline_sw && (
              <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 rounded-sm">
                Baseline: {project.baseline_sw}
              </Badge>
            )}
            {project.vehicle_code && (
              <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 rounded-sm">
                Vehicle: {project.vehicle_code}
              </Badge>
            )}
            {project.asil_level && (
              <Badge className="text-xs bg-[#003057]/10 text-[#003057] border-0 rounded-sm">
                {project.asil_level}
              </Badge>
            )}
            {project.test_level && (
              <Badge variant="outline" className="text-xs border-slate-300 text-slate-600 rounded-sm">
                {project.test_level}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="analyze-btn"
            onClick={handleAnalyze}
            disabled={analyzing || uploads.length === 0}
            variant="outline"
            className="rounded-sm border-[#003057] text-[#003057] hover:bg-[#003057]/5"
          >
            {analyzing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Brain className="w-4 h-4 mr-1" />}
            {analyzing ? "Analyzing..." : "Run Analysis"}
          </Button>
          <Button
            data-testid="generate-report-btn"
            onClick={handleGenerateReport}
            disabled={generating}
            className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm"
          >
            {generating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <FileBarChart className="w-4 h-4 mr-1" />}
            {generating ? "Generating..." : "Generate Report"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="project-tabs">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-0.5">
          <TabsTrigger value="files" className="rounded-sm text-xs data-[state=active]:bg-[#003057] data-[state=active]:text-white" data-testid="tab-files">
            Files ({uploads.length})
          </TabsTrigger>
          <TabsTrigger value="analysis" className="rounded-sm text-xs data-[state=active]:bg-[#003057] data-[state=active]:text-white" data-testid="tab-analysis">
            Analysis ({analyses.length})
          </TabsTrigger>
          <TabsTrigger value="reports" className="rounded-sm text-xs data-[state=active]:bg-[#003057] data-[state=active]:text-white" data-testid="tab-reports">
            Reports ({reports.length})
          </TabsTrigger>
          <TabsTrigger value="diary" className="rounded-sm text-xs data-[state=active]:bg-[#003057] data-[state=active]:text-white" data-testid="tab-diary">
            Diary
          </TabsTrigger>
        </TabsList>

        {/* Files Tab */}
        <TabsContent value="files" className="mt-4 space-y-4" data-testid="files-tab-content">
          <FileUploadZone
            files={pendingFiles}
            setFiles={setPendingFiles}
            descriptions={fileDescriptions}
            setDescriptions={setFileDescriptions}
            onUpload={handleUpload}
            uploading={uploading}
          />
          {uploads.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <CardHeader className="border-b border-slate-100 p-4">
                <CardTitle className="text-sm font-semibold text-[#003057]">
                  Uploaded Files ({uploads.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {uploads.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50 transition-colors"
                      data-testid={`uploaded-file-${file.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <FileText className="w-4 h-4 text-[#003057] shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-700 truncate">
                            {file.original_filename}
                          </p>
                          {file.metadata?.description && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                              {file.metadata.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">{file.file_type}</span>
                            <span className="text-xs text-slate-400">
                              {(file.file_size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] h-5 border-slate-300 text-slate-500">
                          {file.file_category}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFile(file.id)}
                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                          data-testid={`delete-file-${file.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="mt-4 space-y-4" data-testid="analysis-tab-content">
          {analyses.length === 0 ? (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <CardContent className="p-8 text-center">
                <Brain className="w-10 h-10 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">No analyses yet. Upload files and run analysis.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Latest Analysis Summary */}
              {latestAnalysis && (
                <div className="space-y-4">
                  <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
                    <CardHeader className="border-b border-slate-100 p-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-semibold text-[#003057]">
                          Latest Analysis
                        </CardTitle>
                        <Badge
                          className={`text-xs rounded-full ${
                            latestAnalysis.pass_rate >= 80
                              ? "bg-green-100 text-green-700 border-green-200"
                              : latestAnalysis.pass_rate >= 50
                              ? "bg-orange-100 text-orange-700 border-orange-200"
                              : "bg-red-100 text-red-700 border-red-200"
                          }`}
                        >
                          {latestAnalysis.pass_rate}% Pass Rate
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 space-y-4">
                      {/* Stats row */}
                      <div className="grid grid-cols-4 gap-3">
                        <div className="text-center p-3 bg-slate-50 rounded-sm">
                          <p className="text-lg font-bold text-[#003057]">{latestAnalysis.total_tests}</p>
                          <p className="text-xs text-slate-500">Total Tests</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-sm">
                          <p className="text-lg font-bold text-green-700">{latestAnalysis.pass_count}</p>
                          <p className="text-xs text-slate-500">Passed</p>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-sm">
                          <p className="text-lg font-bold text-red-700">{latestAnalysis.fail_count}</p>
                          <p className="text-xs text-slate-500">Failed</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded-sm">
                          <p className="text-lg font-bold text-orange-700">{latestAnalysis.warning_count}</p>
                          <p className="text-xs text-slate-500">Warnings</p>
                        </div>
                      </div>

                      {/* Pass rate bar */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-500">Overall Pass Rate</span>
                          <span className="text-xs font-semibold text-[#003057]">{latestAnalysis.pass_rate}%</span>
                        </div>
                        <Progress value={latestAnalysis.pass_rate} className="h-2" />
                      </div>

                      {/* Summary */}
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                          Summary
                        </h4>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {latestAnalysis.summary}
                        </p>
                      </div>

                      {/* Anomalies */}
                      {latestAnalysis.anomalies?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Anomalies ({latestAnalysis.anomalies.length})
                          </h4>
                          <div className="space-y-2">
                            {latestAnalysis.anomalies.map((a, i) => (
                              <div key={i} className="flex items-start gap-2 p-2 bg-slate-50 rounded-sm">
                                <AlertTriangle
                                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                                    a.severity === "high" ? "text-red-500" :
                                    a.severity === "medium" ? "text-orange-500" : "text-yellow-500"
                                  }`}
                                  strokeWidth={1.5}
                                />
                                <div>
                                  <p className="text-sm font-medium text-slate-700">{a.title}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">{a.description}</p>
                                  {a.recommendation && (
                                    <p className="text-xs text-[#266497] mt-1">Rec: {a.recommendation}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Safety Assessment */}
                      {latestAnalysis.safety_assessment && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Safety Assessment
                          </h4>
                          <p className="text-sm text-slate-700">{latestAnalysis.safety_assessment}</p>
                        </div>
                      )}

                      {/* Compliance Notes */}
                      {latestAnalysis.compliance_notes && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Compliance Notes
                          </h4>
                          <p className="text-sm text-slate-700">{latestAnalysis.compliance_notes}</p>
                        </div>
                      )}

                      {/* Recommendations */}
                      {latestAnalysis.recommendations?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Recommendations
                          </h4>
                          <ul className="space-y-1">
                            {latestAnalysis.recommendations.map((r, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-[#003057] shrink-0" />
                                {r}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-4 space-y-4" data-testid="reports-tab-content">
          {reports.length === 0 && !activeReport ? (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <CardContent className="p-8 text-center">
                <FileBarChart className="w-10 h-10 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">No reports yet. Generate a report from your analysis.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-12 gap-4">
              {/* Report list sidebar */}
              <div className="col-span-3">
                <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
                  <CardHeader className="border-b border-slate-100 p-3">
                    <CardTitle className="text-xs font-semibold text-[#003057]">Reports</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="max-h-[600px]">
                      {reports.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => handleViewReport(r.id)}
                          data-testid={`report-item-${r.id}`}
                          className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                            activeReport?.id === r.id ? "bg-[#003057]/5 border-l-2 border-l-[#003057]" : ""
                          }`}
                        >
                          <p className="text-xs font-medium text-slate-700 truncate">{r.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              className={`text-[9px] h-4 px-1 ${
                                r.status === "completed"
                                  ? "bg-green-100 text-green-700 border-0"
                                  : "bg-yellow-100 text-yellow-700 border-0"
                              }`}
                            >
                              {r.status}
                            </Badge>
                            <span className="text-[10px] text-slate-400">
                              {new Date(r.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </button>
                      ))}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Report view */}
              <div className="col-span-9">
                {activeReport ? (
                  <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
                    <CardHeader className="border-b border-slate-100 p-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-semibold text-[#003057]">
                          {activeReport.title}
                        </CardTitle>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="export-pdf-btn"
                            onClick={() => handleExport(activeReport.id, "pdf")}
                            className="rounded-sm text-xs border-slate-300"
                          >
                            <Download className="w-3.5 h-3.5 mr-1" /> PDF
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="export-docx-btn"
                            onClick={() => handleExport(activeReport.id, "docx")}
                            className="rounded-sm text-xs border-slate-300"
                          >
                            <Download className="w-3.5 h-3.5 mr-1" /> DOCX
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="max-h-[700px]">
                        <div className="report-sheet m-4">
                          {Object.entries(SECTION_LABELS).map(([key, label]) => {
                            const content = activeReport.sections?.[key];
                            if (!content) return null;
                            return (
                              <div key={key} className="mb-6" data-testid={`report-section-${key}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <h2 className="text-lg font-semibold text-[#003057]">{label}</h2>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`regenerate-${key}`}
                                    onClick={() => handleRegenerateSection(activeReport.id, key)}
                                    disabled={regenerating === key}
                                    className="text-xs text-slate-400 hover:text-[#003057] h-7"
                                  >
                                    <RefreshCw className={`w-3 h-3 mr-1 ${regenerating === key ? "animate-spin" : ""}`} />
                                    Regenerate
                                  </Button>
                                </div>
                                <div className="prose prose-sm prose-slate max-w-none text-sm leading-relaxed text-slate-700">
                                  <ReactMarkdown>{content}</ReactMarkdown>
                                </div>
                                <Separator className="mt-6" />
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
                    <CardContent className="p-8 text-center">
                      <p className="text-sm text-slate-500">Select a report to view</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Diary Tab */}
        <TabsContent value="diary" className="mt-4" data-testid="diary-tab-content">
          <DiaryPage projectId={projectId} embedded />
        </TabsContent>
      </Tabs>

      {/* Floating AI Chat */}
      <ProjectChat projectId={projectId} projectName={project.name} />
    </div>
  );
}
